<template>
  <v-simple-table>
    <thead>
      <tr>
        <th v-for="field in tableFields" :key="field.key"> {{ field.label }}</th>
      </tr>
    </thead>
    <tbody v-if="!loaded">
      <tr>
        <td class="text-center" align-v="center" :colspan="tableFields.length"></td>
      </tr>
    </tbody>
    <tbody v-else-if="isEmpty(recentVisits)">
      <tr>
        <td class="text-center" align-v="center" :colspan="tableFields.length">
          <p class="mt-2"><em>{{ $t('location_not_visited_recently') }}</em></p>
        </td>
      </tr>
    </tbody>
    <tbody v-else>
      <tr v-for="routeLocation in recentVisits" v-bind:key="routeLocation.id">
        <td>
          <router-link :to="`/route/${routeLocation.route.id}`">
            <v-icon>mdi-road-variant</v-icon>
            {{ routeLocation.route.name }}
          </router-link>
        </td>
        <td>
          <router-link :to="`/vehicle/${routeLocation.route.vehicle.id}`">
            <v-icon>mdi-truck</v-icon>
            {{ routeLocation.route.vehicle.name }}
          </router-link>
        </td>
        <td v-for="routeProductColumn in routeProductColumns" :key="routeProductColumn">
          {{ formatTemperature(routeLocation, routeProductColumn) }}
        </td>
        <td>
          <if-set :value="routeLocation.vehicle_arrival">
            <multi-time-display :time="routeLocation.vehicle_arrival"/>
          </if-set>
        </td>
        <td>
          <if-set :value="routeLocation.vehicle_departure">
            <multi-time-display :time="routeLocation.vehicle_departure"/>
          </if-set>
        </td>
        <td>
          <if-set :value="routeLocation.vehicle_stop_duration">
            {{
              routeLocation.getVehicleStopDuration() | humanizeDuration(({numberOfSignificantParts: 2, separator: ', '}))
            }}
          </if-set>
        </td>
      </tr>
    </tbody>
  </v-simple-table>
</template>

<script>
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import IfSet from '@/components/shared/IfSet.vue'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import { isEmpty, each, get } from 'lodash'

export default {
  name: 'RecentVisits',
  components: {
    MultiTimeDisplay,
    IfSet
  },
  mixins: [
    DateTimeUtilsMixin,
    TemperatureMixin,
    CurrentUserMixin
  ],
  props: {
    location: Object
  },
  data () {
    return {
      recentVisits: null,
      loaded: false
    }
  },
  created () {
    this.initialize()
  },
  methods: {
    get: get,
    isEmpty: isEmpty,
    initialize () {
      this.fetchData()
    },
    /**
     * Fetch route locations that match the provided location.
     */
    fetchData () {
      const fields = [
        'vehicle_arrival',
        'vehicle_departure',
        'route.id',
        'route.name',
        'route.vehicle.id',
        'route.vehicle.name',
        'route.route_products.product_id',
        'route.route_products.temperature_scale',
        'route.route_products.product.name',
        'route.route_products.product.zone',
        'route.route_products.verofresh_temperature'
      ]
      const expand = [
        'route',
        'route.vehicle',
        'route.route_products.product'
      ]
      const requestParams = {
        location_id: this.location.id,
        vehicle_arrival_after: '-7 day',
        fields: fields.join(','),
        expand: expand.join(',')
      }
      this.$orm('RouteLocation')
        .findAll(requestParams)
        .then(recentVisits => {
          this.recentVisits = recentVisits.getItems()
          this.loaded = true
        })
    },
    formatTemperature (routeLocation, routeProductColumn) {
      const routeProduct = get(routeLocation.route, `product_${routeProductColumn}`)
      if (routeProduct) {
        return this.temperatureToCustomerScale(routeProduct.verofresh_temperature, routeProduct.temperature_scale, this.customerContext.tempFormat()) + ' ˚' + this.customerContext.tempFormat()
      }
      return null
    }
  },
  computed: {
    /**
     * Creates the list of fields for the table. (Including Route Products)
     * @returns {[]}
     */
    tableFields () {
      const fields = []
      fields.push({
        key: 'route',
        label: this.$tc('route')
      })
      fields.push({
        key: 'vehicle',
        label: this.$tc('vehicle')
      })

      const knownRouteProducts = {}
      each(this.recentVisits, visit => {
        each(get(visit.route, 'route_products', []), function (routeProduct) {
          knownRouteProducts[routeProduct.product_id] = routeProduct
        })
      })
      each(knownRouteProducts, function (routeProduct) {
        fields.push({
          key: `indexed_route_products.${routeProduct.product_id}`,
          label: routeProduct.product.name
        })
      })

      fields.push({
        key: 'vehicle_arrival',
        label: this.$tc('arrival')
      })
      fields.push({
        key: 'vehicle_departure',
        label: this.$tc('departure')
      })
      fields.push({
        key: 'vehicle_stop_duration',
        label: this.$tc('duration')
      })
      return fields
    },

    /**
     * Returns an array of the route products contained in the results.
     * Used to create and arrange the route product columns in the table.
     *
     * @return Array
     */
    routeProductColumns () {
      const routeProductColumns = []
      this.tableFields.forEach(function (field) {
        if (field.key.match(/indexed_route_products.[0-9]*/g)) {
          routeProductColumns.push(field.key)
        }
      })
      return routeProductColumns
    }
  }
}
</script>

<style scoped>

</style>
